import { CurrencyInput } from "../../../../components/CurrencyInput";
import { ExpenditureDebug } from "../../../../components/ExpenditureDebug";
import { InfoIcon } from "../../../../components/ui/Tooltip";
import { ExpenditureDetails, HouseholdComposition, BackgroundProperties } from "../../../../types/mortgage";

interface CurrencyInputWithInfoProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder: string;
  tooltip: string;
}

function CurrencyInputWithInfo({ label, value, onChange, placeholder, tooltip }: CurrencyInputWithInfoProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <label className="block text-sm font-subheader text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <InfoIcon tooltip={tooltip} />
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400 sm:text-sm">£</span>
        </div>
        <input
          type="text"
          value={value === 0 ? "" : (value / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/[^\d.]/g, "");
            const parsedValue = parseFloat(numericValue) || 0;
            onChange(Math.round(parsedValue * 100));
          }}
          placeholder={placeholder}
          className="block w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
        />
      </div>
    </div>
  );
}

interface MonthlyCommitmentsFormProps {
  household: HouseholdComposition;
  expenditure: ExpenditureDetails;
  background: BackgroundProperties; // Only needed for debug component
  onExpenditureChange: (expenditure: ExpenditureDetails) => void;
  householdExpenditure: any;
  totalCostsCalculations: any;
  onsMatrix: any;
  isAdmin?: boolean;
}

export function MonthlyCommitmentsForm({ 
  household,
  expenditure, 
  background,
  onExpenditureChange, 
  householdExpenditure,
  totalCostsCalculations,
  onsMatrix,
  isAdmin = false
}: MonthlyCommitmentsFormProps) {
  const updateCommitmentField = <K extends keyof typeof expenditure.commitments>(
    field: K, 
    value: typeof expenditure.commitments[K]
  ) => {
    onExpenditureChange({
      ...expenditure,
      commitments: { ...expenditure.commitments, [field]: value }
    });
  };



  return (
    <div className="space-y-6">
      {/* Household Expenditure Override */}
      <div>
        <h4 className="text-lg font-subheader text-gray-900 dark:text-gray-100 mb-4">Household Expenditure</h4>
        <div className="grid grid-cols-1 gap-6">
          <CurrencyInputWithInfo
            label="Household ONS Override"
            value={expenditure.householdOnsOverride}
            onChange={(value) => onExpenditureChange({ ...expenditure, householdOnsOverride: value })}
            placeholder="0.00"
            tooltip="Enter the monthly cost for household expenses. For this calculator we will use the greater of the figure entered here, or ONS expenditure data for the household size."
          />
        </div>
      </div>

      {/* Monthly Commitments */}
      <div>
        <h4 className="text-lg font-subheader text-gray-900 dark:text-gray-100 mb-4">Monthly Commitments</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CurrencyInputWithInfo
            label="Credit Commitments"
            value={expenditure.commitments.creditCommitments}
            onChange={(value) => updateCommitmentField("creditCommitments", value)}
            placeholder="0.00"
            tooltip="Enter the monthly payment on any credit commitments the client will have other than the MHBS mortgage."
          />

          <CurrencyInputWithInfo
            label="Additional Commitments"
            value={expenditure.commitments.additionalCommitments}
            onChange={(value) => updateCommitmentField("additionalCommitments", value)}
            placeholder="0.00"
            tooltip="Enter the monthly amount of any other expenses not detailed elsewhere in this section – e.g. travel, childcare, maintenance payments"
          />

          <CurrencyInputWithInfo
            label="Pension Contributions"
            value={expenditure.commitments.pensionContributions}
            onChange={(value) => updateCommitmentField("pensionContributions", value)}
            placeholder="0.00"
            tooltip="If the client makes additional contributions not netted from income, enter the monthly amount here"
          />

          <CurrencyInputWithInfo
            label="Other Mortgages"
            value={expenditure.commitments.otherMortgages}
            onChange={(value) => updateCommitmentField("otherMortgages", value)}
            placeholder="0.00"
            tooltip="If the client will have ongoing mortgage costs for other properties (Buy to let, holiday let, second home etc.), include the total of all monthly payments"
          />
        </div>
      </div>




     
      {/* Debug Information */}
      {isAdmin && (
        <ExpenditureDebug 
          household={household}
          expenditure={expenditure}
          background={background}
          householdExpenditure={householdExpenditure}
          totalCostsCalculations={totalCostsCalculations}
          onsMatrix={onsMatrix}
        />
      )}
    </div>
  );
}
